import type { SpanStatus } from './types';

export type LlmCallType =
  | 'prompt'
  | 'auth'
  | 'policy'
  | 'firewall'
  | 'intent'
  | 'orchestration'
  | 'rag'
  | 'llm'
  | 'mcp'
  | 'validation'
  | 'response';

export interface LlmInspectorCall {
  id: string;
  seq: number;
  type: LlmCallType;
  from: string;
  to: string;
  label: string;
  summary: string;
  duration: string;
  status: SpanStatus;
  tokens?: number;
  cost?: string;
  model?: string;
  provider?: string;
  /** Flow step ids to include in simulation for this hop */
  stepIds: string[];
  /** Primary step highlighted in simulation */
  focusStepId: string;
  promptPreview?: string;
}

export const LLM_INSPECTOR_CALLS: LlmInspectorCall[] = [
  {
    id: 'call-01',
    seq: 1,
    type: 'prompt',
    from: 'User (Chat UI)',
    to: 'Finance Copilot',
    label: 'Prompt ingress',
    summary: 'User query received and routed to agent',
    duration: '4ms',
    status: 'success',
    stepIds: ['prompt', 'auth'],
    focusStepId: 'prompt',
    promptPreview: 'Analyze Q3 revenue trends and compare to forecast...',
  },
  {
    id: 'call-02',
    seq: 2,
    type: 'auth',
    from: 'API Gateway',
    to: 'Resource Access',
    label: 'Auth & tenant context',
    summary: 'JWT validated, tenant and department scoped',
    duration: '38ms',
    status: 'success',
    stepIds: ['prompt', 'auth', 'firewall'],
    focusStepId: 'auth',
  },
  {
    id: 'call-03',
    seq: 3,
    type: 'firewall',
    from: 'Finance Copilot',
    to: 'Prompt Firewall',
    label: 'Prompt firewall scan',
    summary: 'Threat scan · Financial domain · Allow',
    duration: '86ms',
    status: 'success',
    stepIds: ['auth', 'firewall', 'policy'],
    focusStepId: 'firewall',
  },
  {
    id: 'call-04',
    seq: 4,
    type: 'policy',
    from: 'Prompt Firewall',
    to: 'AI Governance',
    label: 'Policy validation',
    summary: 'Financial Strict v2.4 · Allow · mask PII on output',
    duration: '42ms',
    status: 'success',
    stepIds: ['firewall', 'policy', 'intent'],
    focusStepId: 'policy',
  },
  {
    id: 'call-05',
    seq: 5,
    type: 'intent',
    from: 'Policy Engine',
    to: 'Intent Orchestration',
    label: 'Intent detection',
    summary: 'financial_analysis · confidence 91%',
    duration: '124ms',
    status: 'success',
    stepIds: ['policy', 'intent', 'orchestration'],
    focusStepId: 'intent',
  },
  {
    id: 'call-06',
    seq: 6,
    type: 'orchestration',
    from: 'Intent Router',
    to: 'Agent Orchestrations',
    label: 'Orchestration match',
    summary: 'fin-revenue-analysis-v3 · 5-step DAG · 2 LLM calls',
    duration: '30ms',
    status: 'success',
    stepIds: ['intent', 'orchestration', 'plan', 'rag'],
    focusStepId: 'orchestration',
  },
  {
    id: 'call-07',
    seq: 7,
    type: 'rag',
    from: 'Orchestration',
    to: 'Knowledge Workspace',
    label: 'Knowledge retrieval',
    summary: 'Finance KB · 5 chunks · Pinecone',
    duration: '486ms',
    status: 'success',
    tokens: 420,
    cost: '$0.008',
    stepIds: ['plan', 'rag', 'llm1'],
    focusStepId: 'rag',
  },
  {
    id: 'call-08',
    seq: 8,
    type: 'llm',
    from: 'Orchestration',
    to: 'OpenAI GPT-4o-mini',
    label: 'LLM call #1 — Structure',
    summary: 'Query planning · entity extraction',
    duration: '412ms',
    status: 'success',
    tokens: 840,
    cost: '$0.012',
    model: 'GPT-4o-mini',
    provider: 'OpenAI',
    stepIds: ['rag', 'llm1', 'mcp'],
    focusStepId: 'llm1',
  },
  {
    id: 'call-09',
    seq: 9,
    type: 'mcp',
    from: 'LLM Planner',
    to: 'MCP CustomerDB',
    label: 'MCP tool call',
    summary: 'CustomerDB.Read · 3 records',
    duration: '186ms',
    status: 'success',
    stepIds: ['llm1', 'mcp', 'llm2'],
    focusStepId: 'mcp',
  },
  {
    id: 'call-10',
    seq: 10,
    type: 'llm',
    from: 'Orchestration',
    to: 'OpenAI GPT-4o',
    label: 'LLM call #2 — Synthesize',
    summary: 'Final analysis with RAG + MCP context',
    duration: '1,482ms',
    status: 'success',
    tokens: 3420,
    cost: '$0.068',
    model: 'GPT-4o',
    provider: 'OpenAI',
    stepIds: ['mcp', 'llm2', 'validate'],
    focusStepId: 'llm2',
  },
  {
    id: 'call-11',
    seq: 11,
    type: 'validation',
    from: 'GPT-4o',
    to: 'Output Firewall',
    label: 'Output validation',
    summary: 'PII masked · compliance pass',
    duration: '64ms',
    status: 'success',
    stepIds: ['llm2', 'validate', 'response'],
    focusStepId: 'validate',
  },
  {
    id: 'call-12',
    seq: 12,
    type: 'response',
    from: 'Output Validator',
    to: 'User',
    label: 'Final response',
    summary: 'Streamed to client · audit logged',
    duration: '8ms',
    status: 'success',
    tokens: 4680,
    cost: '$0.084',
    stepIds: ['validate', 'response'],
    focusStepId: 'response',
  },
];

export const CALL_TYPE_LABELS: Record<LlmCallType, string> = {
  prompt: 'Prompt',
  auth: 'Auth',
  policy: 'Policy',
  firewall: 'Firewall',
  intent: 'Intent',
  orchestration: 'Orchestration',
  rag: 'RAG',
  llm: 'LLM',
  mcp: 'MCP',
  validation: 'Validation',
  response: 'Response',
};

export const CALL_TYPE_COLORS: Record<LlmCallType, string> = {
  prompt: 'bg-blue-500/15 text-blue-500 border-blue-500/25',
  auth: 'bg-slate-500/15 text-slate-500 border-slate-500/25',
  policy: 'bg-amber-500/15 text-amber-500 border-amber-500/25',
  firewall: 'bg-amber-500/15 text-amber-600 border-amber-500/25',
  intent: 'bg-indigo-500/15 text-indigo-500 border-indigo-500/25',
  orchestration: 'bg-violet-500/15 text-violet-500 border-violet-500/25',
  rag: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/25',
  llm: 'bg-purple-500/15 text-purple-500 border-purple-500/25',
  mcp: 'bg-teal-500/15 text-teal-500 border-teal-500/25',
  validation: 'bg-orange-500/15 text-orange-500 border-orange-500/25',
  response: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/25',
};
